import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLink,
  faKey,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'
import useRoleRemediation from 'api/hooks/use-role-remediation'
import { useRolePermissions } from 'api/hooks/use-role-permissions'
import { useRoleEmployees } from 'api/hooks/use-role-employees'

function RemediationModal({ show, setShow, role }) {
  const handleClose = () => setShow(false)

  const { data, isLoading, isError } = useRoleRemediation({
    roleId: role.id,
    options: { enabled: !!show }
  })

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Remediation Analysis — {role.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center my-5 py-5">
            <Spinner animation="grow" variant="warning" />
          </div>
        )}

        {isError && (
          <Alert variant="danger">Error loading remediation</Alert>
        )}

        {!isLoading && !isError && data && (
          <RemediationContent data={data} role={role} />
        )}
      </Modal.Body>
    </Modal>
  )
}

function RemediationContent({ data, role }) {
  const [testPerm, setTestPerm] = useState(null)
  const [approachIdx, setApproachIdx] = useState(0)

  // Role permissions carry the human-readable caption (permission.description)
  // keyed by permission.name === the remediation permission_key. The Lambda's
  // own `caption` can be blank, so resolve the label from thirdline here.
  const { data: rolePermissions } = useRolePermissions({
    params: { role_id: role.id, page: 1, perPage: 1000 }
  })

  const permLabel = (permissionKey, category) => {
    const perms = rolePermissions?.data ?? []
    const match =
      (category &&
        perms.find(
          (rp) =>
            rp.permission.name === permissionKey &&
            rp.permission.category === category
        )) ||
      perms.find((rp) => rp.permission.name === permissionKey)
    return match?.permission?.description || match?.permission?.category_caption
  }

  const {
    summary,
    minimum_vertex_cover = {},
    decision_guide = {},
    conflict_pairs = []
  } = data

  if (!summary?.has_conflicts) {
    return (
      <Card className="border">
        <Card.Body className="text-center py-5">
          <div className="h6 mb-1">No internal conflicts</div>
          <div className="text-muted">
            This role has no Segregation-of-Duties conflicts to remediate.
          </div>
        </Card.Body>
      </Card>
    )
  }

  const isSelected = (p) =>
    testPerm?.permission_key === p.permission_key &&
    testPerm?.category === p.category

  // Group conflict pairs by the conflicting process pair so the two processes
  // head each group once. Pairs are NOT deduped — a permission appears on every
  // pair it participates in.
  const conflictGroups = Object.values(
    conflict_pairs.reduce((acc, c) => {
      const key = c.analytic_key || `${c.left.mastvar}|${c.right.mastvar}`
      if (!acc[key]) {
        acc[key] = {
          key,
          leftProcess: c.left.mastvar,
          rightProcess: c.right.mastvar,
          pairs: []
        }
      }
      acc[key].pairs.push(c)
      return acc
    }, {})
  )

  // Remediation approaches: the recommended minimum set (index 0) followed by
  // each cumulative whole-function removal step.
  const strategies = decision_guide.strategies ?? []
  const removalPath = decision_guide.function_removal_path ?? []
  const selectedStrategy = strategies[approachIdx] ?? strategies[0]
  const totalPairs = summary.total_conflict_pairs
  const pct = selectedStrategy?.pct_resolved ?? 0
  const fullyResolved = pct >= 100

  const mvcByKey = new Map(
    (minimum_vertex_cover.permissions ?? []).map((p) => [p.permission_key, p])
  )
  const permRecord = (key) =>
    (rolePermissions?.data ?? []).find((rp) => rp.permission.name === key)
      ?.permission

  // Permissions the selected approach would remove.
  const approachRows =
    approachIdx === 0
      ? [...(minimum_vertex_cover.permissions ?? [])].sort(
          (a, b) =>
            (b.conflict_pairs_covered ?? 0) - (a.conflict_pairs_covered ?? 0)
        )
      : removalPath.slice(0, approachIdx).flatMap((step) =>
          (step.permission_keys ?? []).map((key) => {
            const info = permRecord(key)
            return {
              permission_key: key,
              mastvar: step.mastvar,
              caption: info?.description || info?.category_caption || key,
              category: info?.category || '',
              conflict_pairs_covered: mvcByKey.get(key)?.conflict_pairs_covered
            }
          })
        )

  const approachLabel = (s, i) => {
    if (i === 0)
      return 'Minimum set of permissions to resolve all conflicts'
    if (i === 1) return `Remove ${removalPath[0]?.mastvar} (highest impact)`
    return `Remove top ${i} functions (through ${removalPath[i - 1]?.mastvar})`
  }

  const selectApproach = (i) => {
    setApproachIdx(i)
    setTestPerm(null)
  }

  return (
    <>
      {/* Explainer */}
      <p className="text-muted mb-3">
        This role lets one person do tasks that should be kept separate. The
        numbers below show how many <strong>internal conflicts</strong> the role
        has, how many <strong>pairs of permissions</strong> clash, and the
        fewest <strong>permissions you&apos;d need to remove</strong> to clear
        them all. Use the tables below to see which permissions to remove, test
        who would lose access, and review each conflict.
      </p>

      {/* Summary */}
      <Row className="g-3 mb-3">
        {[
          {
            value: summary.sod_rules_triggered,
            label: 'Internal Conflicts',
            icon: faExclamationTriangle,
            color: 'danger'
          },
          {
            value: summary.total_conflict_pairs,
            label: 'Conflicting permission pairs',
            icon: faLink,
            color: 'warning'
          },
          {
            value: summary.mvc_size,
            label: 'Permissions to remove',
            icon: faKey,
            color: 'primary'
          }
        ].map((s) => (
          <Col md={4} key={s.label}>
            <Card className="border h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-${s.color} bg-opacity-10 text-${s.color}`}
                  style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
                >
                  <FontAwesomeIcon icon={s.icon} className="fs-5" />
                </div>
                <div>
                  <div className="h3 mb-0 fw-bold">{s.value}</div>
                  <small className="text-muted">{s.label}</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Permissions to remove — by selected approach */}
      <Card className="border mb-3">
        <Card.Header className="bg-white">
          <div className="fw-semibold">Permissions to remove</div>
          <small className="text-muted">
            Pick a remediation approach to see which permissions it removes and
            how many conflicts it resolves. Click a row to see who would lose
            access.
          </small>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3" controlId="remediation-approach">
            <Form.Label className="fw-semibold mb-1">Approach</Form.Label>
            <Form.Select
              value={approachIdx}
              onChange={(e) => selectApproach(Number(e.target.value))}
            >
              {strategies.map((s, i) => (
                <option key={i} value={i}>
                  {approachLabel(s, i)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedStrategy && (
            <div
              className={`d-flex align-items-center gap-3 p-3 rounded mb-3 ${
                fullyResolved
                  ? 'bg-success bg-opacity-10'
                  : 'bg-info bg-opacity-10'
              }`}
            >
              <FontAwesomeIcon
                icon={fullyResolved ? faCheckCircle : faInfoCircle}
                className={fullyResolved ? 'text-success' : 'text-info'}
                size="lg"
              />
              <div>
                Removing{' '}
                <strong>{selectedStrategy.permissions_removed}</strong>{' '}
                permission
                {selectedStrategy.permissions_removed === 1 ? '' : 's'} resolves{' '}
                <strong>{selectedStrategy.pairs_resolved}</strong> of{' '}
                <strong>{totalPairs}</strong> conflicting permission pairs (
                <strong>{pct}%</strong>)
                {fullyResolved
                  ? ' — every conflict cleared.'
                  : ` — ${
                      totalPairs - selectedStrategy.pairs_resolved
                    } still unresolved.`}
              </div>
            </div>
          )}

          <Table size="sm">
            <thead>
              <tr>
                <th>Permission</th>
                <th>Function</th>
                <th>Category</th>
                <th className="text-center">Conflict pairs covered</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {approachRows.map((p) => (
                <React.Fragment key={p.permission_key}>
                  <tr
                    onClick={() => setTestPerm(isSelected(p) ? null : p)}
                    className={isSelected(p) ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div>
                        {permLabel(p.permission_key, p.category) ||
                          p.caption ||
                          p.permission_key}
                      </div>
                      <code className="text-muted small">
                        {p.permission_key}
                      </code>
                    </td>
                    <td>{p.mastvar}</td>
                    <td>{p.category}</td>
                    <td className="text-center">
                      {p.conflict_pairs_covered ?? '—'}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant={isSelected(p) ? 'primary' : 'light'}
                        className={isSelected(p) ? '' : 'text-primary'}
                        tabIndex={-1}
                      >
                        Test removal
                      </Button>
                    </td>
                  </tr>
                  {isSelected(p) && (
                    <tr>
                      <td colSpan={5}>
                        <RemovalImpact role={role} perm={p} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Conflict pairs */}
      <Card className="border">
        <Card.Header className="bg-white">
          <div className="fw-semibold">Conflict pairs ({conflict_pairs.length})</div>
          <small className="text-muted">
            Each pair is two permissions this role holds that shouldn&apos;t be
            combined, grouped by the conflicting processes they belong to.
          </small>
        </Card.Header>
        <Card.Body>
          <Table size="sm">
            <thead>
              <tr>
                <th>Process 1 Permission</th>
                <th>Process 2 Permission</th>
              </tr>
            </thead>
            <tbody>
              {conflictGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <tr className="table-active">
                    <td className="fw-semibold">{g.leftProcess}</td>
                    <td className="fw-semibold">{g.rightProcess}</td>
                  </tr>
                  {g.pairs.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <div>
                          {permLabel(c.left.permission_key) ||
                            c.left.caption ||
                            c.left.permission_key}
                        </div>
                        <code className="text-muted small">
                          {c.left.permission_key}
                        </code>
                      </td>
                      <td>
                        <div>
                          {permLabel(c.right.permission_key) ||
                            c.right.caption ||
                            c.right.permission_key}
                        </div>
                        <code className="text-muted small">
                          {c.right.permission_key}
                        </code>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  )
}

function RemovalImpact({ role, perm }) {
  const { data: rolePermissions, isLoading: permsLoading } = useRolePermissions({
    params: { role_id: role.id, page: 1, perPage: 1000 }
  })

  const resolved =
    rolePermissions?.data?.find(
      (rp) =>
        rp.permission.name === perm.permission_key &&
        rp.permission.category === perm.category
    ) ||
    rolePermissions?.data?.find(
      (rp) => rp.permission.name === perm.permission_key
    )

  const permissionId = resolved?.permission?.id
  const permName =
    resolved?.permission?.description ||
    resolved?.permission?.category_caption ||
    perm.caption ||
    perm.permission_key

  const { data, isLoading: employeesLoading, isSuccess } = useRoleEmployees({
    params: {
      role_id: role.id,
      active: 'true',
      withPermissionRemoved: 'true',
      permission_id: permissionId,
      page: 1,
      perPage: 1000
    },
    options: { enabled: !!permissionId }
  })

  const loading = permsLoading || (!!permissionId && employeesLoading)

  const losers = isSuccess
    ? data.data.filter(
        (a) =>
          (data.new_effective_permissions?.filter(
            (p) => p.employee_id === a.id
          ).length ?? 0) === 0
      )
    : []

  return (
    <div className="px-2 py-4">
      <div className="fw-bold mb-2">
        Users who would lose access if <strong>{permName}</strong> is removed
      </div>

      {loading && (
        <div className="text-center my-3">
          <Spinner animation="grow" variant="warning" size="sm" />
        </div>
      )}

      {!loading && !permissionId && (
        <div className="text-muted">
          Could not match this permission to test removal.
        </div>
      )}

      {!loading && permissionId && isSuccess && losers.length === 0 && (
        <div className="text-muted">
          No users would lose access — everyone retains it through another role.
        </div>
      )}

      {!loading && isSuccess && losers.length > 0 && (
        <Table size="sm" className="mb-0 bg-white">
          <thead>
            <tr>
              <th>User</th>
              <th>Department</th>
            </tr>
          </thead>
          <tbody>
            {losers.map((a) => (
              <tr key={a.id}>
                <td>{a.external_id}</td>
                <td className="text-muted">{a.department_name}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

export default RemediationModal
